import clsx from "clsx";
import style from "./PersonalDetails.module.css";
import { RootState } from "@/store/dtm";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { setMaritalStatus, setNumOfDependents, setCitizenshipStatus, setFirstTimeBuyer, setRecentResident, setDateOfBirthDay, setDateOfBirthMonth, setDateOfBirthYear, setFirstName, setLastName, setTitle, setMiddleName, setSin, setPhoneNumber, setEmailAddress, setPhoneType } from "@/slices/applicantSlice";
import DateComponent from "../shared/DateComponent";
import { isValidSin, transformMonth, validateAge } from "@/lib/util";
import { applicationCategory } from "@/types/applicationType";
import { createApiClient } from "@/lib/Requests";
import { CreateApplicantModel, ApplicantForRegistrationDto, UpdateDealCommand, AddressDto } from "@/lib/DTMWebAPIClient.generated";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import InputMask from "react-input-mask";
import { selectHomeAddress } from "@/slices/homeAddressSlice";
import { selectPreviousAddress } from "@/slices/PreviousAddressSlice";

export default function PersonalDetails(props: any) {
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const email = applicant.isProspect ? useSelector(
    (state: RootState) => state.credential.credential.email
  ) : applicant.email;
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
  const homeAddress = useSelector(selectHomeAddress).address;
  const previousAddresses = useSelector(selectPreviousAddress).addresses;

  const dispatch = useDispatch();
  const [showEdit, setShowEdit] = useState(false);
  const [showSin, setShowSin] = useState(false);
  const [firstTimeHomeBuyer, setFirstTimeHomeBuyer] = useState("");
  const [newToCanada, setNewToCanada] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [ageErrorState, setAgeErrorState] = useState({ hasError: false, errorMessage: "", invalidYear: false, invalidDay: false })
  const [isSaving, setIsSaving] = useState(false);
  const [sinShown, setSinShown] = useState(false);
  const [phoneNumberText, setPhoneNumberText] = useState(applicant.phoneNumber);

  const methods = useForm({
    defaultValues: {
      title: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      phoneType: "",
      sin: "",
      maritalStatus: "",
      year: applicant.dob_year,
      month: applicant.dob_month,
      day: applicant.dob_day,
      numOfDependents: "",
      citizenshipStatus: "",
      firstTimeBuyer: "",
      newToCanada: "",

      applicationPurpose: "",
      applicationType: "",
      term: "",
      amortization: "",
      purchasePrice: "",
      downPayment: "",
      currentPropertyValue: "",
      remainingBalance: ""
    },
  });

  const onSubmit = async (data: any) => {
    if (applicant.isProspect) {
      dispatch(setFirstName(data.firstName));
      dispatch(setLastName(data.lastName));
      dispatch(setMiddleName(data.middleName));
      dispatch(setDateOfBirthYear(data.year));
      dispatch(setDateOfBirthMonth(data.month));
      dispatch(setDateOfBirthDay(data.day));
      dispatch(setSin(data.sin));
    }
    dispatch(setTitle(data.title));
    dispatch(setPhoneNumber(data.phoneNumber));
    dispatch(setPhoneType(data.phoneType));
    dispatch(setEmailAddress(data.email));
    dispatch(setMaritalStatus(data.maritalStatus));
    dispatch(setNumOfDependents(data.numOfDependents));
    dispatch(setCitizenshipStatus(data.citizenshipStatus));
    dispatch(setFirstTimeBuyer(data.firstTimeBuyer));
    dispatch(setRecentResident(data.newToCanada));

    setIsSaving(true);
    let addresses: AddressDto[] = [];
    addresses.push(new AddressDto({
      id: homeAddress.id,
      streetNumber: homeAddress.streetNumber,
      streetName: homeAddress.streetName,
      streetType: homeAddress.streetType,
      streetDirection: homeAddress.streetDirection,
      unitType: homeAddress.unitType,
      unitNumber: homeAddress.unitNumber,
      city: homeAddress.city,
      region: homeAddress.province,
      postalCode: homeAddress.postalCode,
      country: homeAddress.country,
      timeAtResidenceInMonths: (+applicant.residenceYears * 12) + +applicant.residenceMonths,
      residenceStatusType_Name: applicant.residentStatus,
      deliveryMode: undefined,
      modeNumber: undefined,
      deliveryMode2: undefined,
      modeNumber2: undefined,
      isMailing: false
    }));
    previousAddresses.forEach((prevAddress) => {
      addresses.push(new AddressDto({
        id: prevAddress.id,
        streetNumber: prevAddress.streetNumber,
        streetName: prevAddress.streetName,
        streetType: prevAddress.streetType,
        streetDirection: prevAddress.streetDirection,
        unitType: prevAddress.unitType,
        unitNumber: prevAddress.unitNumber,
        city: prevAddress.city,
        region: prevAddress.province,
        postalCode: prevAddress.postalCode,
        country: prevAddress.country,
        timeAtResidenceInMonths: (prevAddress.yearsLivedOn ? +prevAddress.yearsLivedOn * 12 : 0)
          + (prevAddress.monthsLivedOn ? +prevAddress.monthsLivedOn : 0),
        residenceStatusType_Name: undefined,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      }));
    });
    await client.apiApplicantsPut(new CreateApplicantModel({
      applicant: new ApplicantForRegistrationDto({
        cbConsent: false,
        caslConsent: credential.credential.meridianCommunication,
        permanentResident: false,
        title: data.title,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
        phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
        phoneType: data.phoneType,
        sin: data.sin != null && applicant.isProspect ? data.sin.replace(/\s/g, "") : +applicant.sin,
        email: applicant.isProspect ? credential.credential.email : data.email,
        newToCanada: newToCanada === "yes",
        student: false,
        immigrationResident: false,
      }),
      memberNo: 0,
      startDate: dayjs(new Date()),
      creditScore: 0,
      creditBureauRequestedDate: dayjs(new Date()),
      cbConsent: true,
      isStaff: false,
      isPrimary: !application.isCoApplicant,
      title: data.title,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
      phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
      phoneType: data.phoneType,
      sin: data.sin != null && applicant.isProspect ? data.sin.replace(/\s/g, "") : +applicant.sin,
      email: applicant.isProspect ? credential.credential.email : data.email,
      newToCanada: newToCanada === "yes",
      student: false,
      immigrationResident: false,
      maritalStatus: data.maritalStatus,
      citizenshipStatus: data.citizenshipStatus,
      numberOfDependents: data.numOfDependents,
      address: addresses
    })).then((result) => {
      console.log(result);
      updateIsFirstTimeHomeBuyer(data.firstTimeBuyer == "yes");
    }).catch((error) => {
      setIsSaving(false);
      console.log("Error updating applicant", error);
    });
  };

  const updateIsFirstTimeHomeBuyer = async (isFirstTimeHomeBuyer: boolean) => {
    await client.apiDealsPut(new UpdateDealCommand({
      firstTimeHomeBuyer: isFirstTimeHomeBuyer,
      relationship: application.jointAccepted ? application.jointRelationship : "Owner",
      primary: !application.jointAccepted
    })).then(async (result) => {
      setIsSaving(false);
      closeEdit();
    }).catch(async (error) => {
      setIsSaving(false);
      console.log("Error updating deal", error);
    });
  }

  const onError = (errors: object) => {
    console.log(errors);
  }

  const toggleSin = () => {
    setShowSin(showSin ? false : true);
  };

  const openEdit = () => {
    setShowEdit(true);
    props.editModeOn(!showEdit);

    methods.setValue("title", applicant.title);
    methods.setValue("firstName", applicant.firstName);
    methods.setValue("middleName", applicant.middleName);
    methods.setValue("lastName", applicant.lastName);
    if (applicant.isProspect) {
      methods.setValue("email", credential.credential.email);
    }else{
      methods.setValue("email", applicant.email);
    }
    setPhoneNumberText(applicant.phoneNumber);
    methods.setValue("phoneNumber", applicant.phoneNumber);
    methods.setValue("phoneType", applicant.phoneType);
    methods.setValue("sin", applicant.sin);

    methods.setValue("maritalStatus", applicant.maritalStatus);
    methods.setValue("numOfDependents", applicant.numOfDependents.toString());
    methods.setValue("citizenshipStatus", applicant.citizenshipStatus);
    methods.setValue("firstTimeBuyer", applicant.firstTimeBuyer);
    setFirstTimeHomeBuyer(applicant.firstTimeBuyer);
    methods.setValue("newToCanada", applicant.recentResident);
    setNewToCanada(applicant.recentResident);
    methods.clearErrors();
  }

  const closeEdit = () => {
    setShowEdit(false);
    setIsSaving(false);
    props.editModeOn(!showEdit);
  }

  const firstTimeBuyerChangeHandler = (e: any) => {
    setFirstTimeHomeBuyer(e.target.value);
    methods.clearErrors("firstTimeBuyer");
    methods.setValue("firstTimeBuyer", e.target.value);
  };
  const recentResidentChangeHandler = (e: any) => {
    setNewToCanada(e.target.value);
    methods.clearErrors("newToCanada");
    methods.setValue("newToCanada", e.target.value);
  };

  const setYearHandler = (e: any) => {
    setDobYear(e.target.value)
  };
  const setMonthHandler = (e: any) => {
    setDobMonth(e.target.value)
  };
  const setDayHandler = (e: any) => {
    setDobDay(e.target.value)
  };
  const validateDay = () => {
    if (ageErrorState.invalidDay) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }
  const validateYear = () => {
    if (ageErrorState.invalidYear) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }
  const toggleSinVisiblity = () => {
    setSinShown(sinShown ? false : true);
  };
  useEffect(() => {
    let dob = dobYear + "/" + dobMonth + "/" + dobDay; //Date of birth in yyyy/mm/dd format
    let validationErrors = validateAge(dob);

    if (!validationErrors.hasError) {
      methods.clearErrors('year')
      methods.clearErrors('month')
      methods.clearErrors('day')
    }

    setAgeErrorState(validationErrors)
    validateDay();
    validateYear();
  }, [dobYear, dobMonth, dobDay]);

  useEffect(() => {
    setDobDay(methods.getValues('day'));
    setDobMonth(methods.getValues('month'));
    setDobYear(methods.getValues('year'));
  }, []);



  return (
    <div className="mt-5">
      <div className="flex flex-row justify-between mt-5">
        <h2>Personal Details</h2>
        {!showEdit && !props.restrictEditing && (
          <h3
            className="brand-blue-text text-xs grid grid-flow-col cursor-pointer"
            onClick={openEdit}
          >
            <img
              src="assets/edit-icon.svg"
              className={style.editIcon}
              alt="edit"
            />
            <span>Edit</span>
          </h3>
        )}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {!showEdit ?
          <motion.div
            key="2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            {applicant?.title && <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Title</p>
                <h3 className="mt-3" style={{ textTransform: 'capitalize' }}>{applicant?.title}.</h3>
              </div>
            </div>
            }
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Legal first name</p>
                <h3 className="mt-3">{applicant?.firstName}</h3>
              </div>
              <div>
                <p className="mt-5 text-sm">Middle name (optional)</p>
                <h3 className="mt-3">{applicant?.middleName.trim() != "" ? applicant?.middleName : "-"}</h3>
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Legal last name</p>
                <h3 className="mt-3">{applicant.lastName}</h3>
              </div>
              <div>
                <p className="mt-5 text-sm">Date of birth</p>
                <h3 className="mt-3">
                  {applicant?.dob_day} {transformMonth(applicant?.dob_month)}{" "}
                  {applicant?.dob_year}
                </h3>
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Email address</p>
                <h3 className="mt-3">{email}</h3>
              </div>
              <div>
                <p className="mt-5 text-sm">Phone number</p>
                <h3 className="mt-3">{applicant?.phoneNumber}</h3>
              </div>
              <div className="md:ml-5">
                <p className={clsx("mt-5 text-sm")}>Phone Type</p>
                <h3 className="mt-3">
                  {applicant?.phoneType.charAt(0).toUpperCase() +
                    applicant?.phoneType.slice(1)}
                </h3>
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              {applicant?.sin && <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Social Insurance Number</p>
                <div className={clsx("grid grid-flow-col", style.sin)}>
                  {showSin ? (
                    <p className={style.sinText + " mt-3"}>{applicant?.sin}</p>
                  ) : (
                    <p className={style.sinText + " mt-3"}>•••••••••</p>
                  )}
                  <a
                    className="brand-blue-text underline mt-3 cursor-pointer"
                    onClick={toggleSin}
                  >
                    {showSin ? "Hide" : "Reveal"}
                  </a>
                </div>
              </div>
              }
              <div>
                <p className="mt-5 text-sm">Marital Status</p>
                <h3 className="mt-3" style={{ textTransform: 'capitalize' }}>{applicant?.maritalStatus}</h3>
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Number of dependents</p>
                <h3 className="mt-3">{applicant?.numOfDependents}</h3>
              </div>
              <div>
                <p className="mt-5 text-sm">Citizenship Status</p>
                {applicant?.citizenshipStatus === "citizen" && <h3 className="mt-3">Canadian Citizen</h3>}
                {applicant?.citizenshipStatus === "resident" && <h3 className="mt-3">Permanent Resident</h3>}
                {applicant?.citizenshipStatus === "workvisa" && <h3 className="mt-3">Work Visa</h3>}
                {applicant?.citizenshipStatus === "studentvisa" && <h3 className="mt-3">Student Visa</h3>}
                {applicant?.citizenshipStatus === "nonresident" && <h3 className="mt-3">Non-Resident</h3>}
                {applicant?.citizenshipStatus === "other" && <h3 className="mt-3">Other</h3>}
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              {application.applicationCategory == applicationCategory.Buy &&
                <div className={style.twoColFirst}>
                  <p className="mt-5 text-sm">Are you a first-time home buyer?</p>
                  <h3 className="mt-3">{applicant?.firstTimeBuyer == "yes" ? "Yes" : "No"}</h3>
                </div>
              }
              <div>
                <p className="mt-5 text-sm">Did you arrive in Canada in the last 5 years?</p>
                <h3 className="mt-3">{applicant?.recentResident == "yes" ? "Yes" : "No"}</h3>
              </div>
            </div>
          </motion.div> :
          <motion.div
            key="3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
              <div className="flex md:flex-row flex-col">
                <div className={style.threeColFirst}>
                  <div className="col-span-1 mb-3">
                    <p className="mt-5 text-sm">Title (optional)</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.title ? "" : "hidden",
                            "w-4 h-4 absolute top-5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="title"
                        {...methods.register("title", {})}
                        defaultValue=""
                        className={clsx(
                          methods.formState?.errors?.title?.message ? "error" : "",
                          style.frequency, "mt-2 pt-2.5"
                        )}
                        style={{ maxWidth: 140 }}
                      >
                        <option hidden value="">
                          Choose...
                        </option>
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="ms">Ms</option>
                        <option value="miss">Miss</option>
                        <option value="dr">Dr</option>
                        <option value="rev">Rev</option>
                      </select>
                    </div>
                  </div>
                  {methods.formState?.errors?.title?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.title.message}
                    </span>
                  )}
                </div>
                {!applicant.isProspect &&
                  <>
                    <div className="">
                      <p className="mt-5 text-sm">Phone number</p>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.phoneNumber
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <InputMask
                          type="text"
                          placeholder="(000) 000-0000"
                          alwaysShowMask={false}
                          maskChar={null}
                          mask="(999) 999-9999"
                          value={phoneNumberText}
                          className={clsx(
                            methods.formState?.errors?.phoneNumber?.message
                              ? "error"
                              : "",
                            "mt-2 w:[200px] md:w-[305px]"
                          )}
                          {...methods.register("phoneNumber", {
                            required: "Enter a valid phone number",
                            pattern: {
                              value:
                                // eslint-disable-next-line no-useless-escape
                                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                              message: "Enter a valid phone number",
                            },
                          })}
                          onChange={(e) => {
                            setPhoneNumberText(e.target.value);
                            methods.setValue("phoneNumber", e.target.value);
                          }}
                        />
                      </div>
                      {methods.formState?.errors?.phoneNumber?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          Enter a valid phone number
                        </span>
                      )}
                    </div>
                    <div className="md:ml-4">
                      <p className="mt-5 text-sm">Phone type</p>
                      <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md mx-auto">
                        <li className="relative">
                          <input
                            {...methods.register("phoneType", {
                              required: "Select a phone type"
                            })}
                            type="radio"
                            value="Mobile"
                            id="mobile"
                            className="sr-only peer "
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.phoneType?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="mobile"
                          >
                            <p className="p-2 brand-blue-text leading-6">Mobile</p>
                          </label>
                        </li>
                        <li className="relative">
                          <input
                            {...methods.register("phoneType", {
                              required: "Select a phone type"
                            })}
                            type="radio"
                            value="Home"
                            id="home"
                            className="sr-only peer leading-6"
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.phoneType?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="home"
                          >
                            <p className="p-2 brand-blue-text leading-6">Home</p>
                          </label>
                        </li>
                      </ul>
                      {methods.formState?.errors?.phoneType?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState?.errors?.phoneType?.message}
                        </span>
                      )}
                    </div>
                  </>
                }
              </div>
              {applicant.isProspect &&
                <>
                  <div className="flex md:flex-row flex-col">
                    <div className={style.twoColFirst}>
                      <p className="mt-5 text-sm">Legal first name</p>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.firstName ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.firstName?.message
                              ? "error"
                              : "",
                            "mt-2 w:[220px] md:w-[305px]"
                          )}
                          type="text"
                          id="firstName"
                          {...methods.register("firstName", {
                            required: "Enter your first name",
                            maxLength: {
                              value: 38,
                              message: "Name exceeds maximum character count",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9 '-.]*$/,
                              message: "Please enter valid first name",
                            },
                            onBlur: () => {
                              methods.setValue("firstName", methods.getValues("firstName").trim());
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.firstName?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.firstName.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="mt-5 text-sm">Middle name (optional)</p>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.middleName ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.middleName?.message
                              ? "error"
                              : "",
                            "mt-2 w:[220px] md:w-[305px]"
                          )}
                          type="text"
                          id="middleName"
                          {...methods.register("middleName", {
                            maxLength: {
                              value: 38,
                              message: "Name exceeds maximum character count",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9 '-.]*$/,
                              message: "Please enter valid middle name",
                            },
                            onBlur: () => {
                              methods.setValue("middleName", methods.getValues("middleName").trim());
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.middleName?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.middleName.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex md:flex-row flex-col">
                    <div>
                      <p className="mt-5 text-sm">Legal last name</p>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.lastName ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.lastName?.message
                              ? "error"
                              : "",
                            "mt-2 w:[220px] md:w-[305px]"
                          )}
                          type="text"
                          id="lastName"
                          {...methods.register("lastName", {
                            required: "Enter your last name",
                            maxLength: {
                              value: 38,
                              message: "Name exceeds maximum character count",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9 '-.]*$/,
                              message: "Please enter valid first name",
                            },
                            onBlur: () => {
                              methods.setValue("lastName", methods.getValues("lastName").trim());
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.lastName?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.lastName.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex md:flex-row flex-col">
                    <div>
                      <p className="mt-5 text-sm">Date of Birth</p>
                      <DateComponent methods={methods} readOnly={!applicant.isProspect} callbackYear={setYearHandler} callbackMonth={setMonthHandler} callbackDay={setDayHandler} validateDay={validateDay} validateYear={validateYear} />
                    </div>
                  </div>
                  {applicant.isProspect &&
                    <div className="col-span-1">
                      <div
                        className={clsx(
                          "flex mt-5",
                          style.Sin
                        )}
                      >
                        <span className="mr-1"> Social Insurance Number (optional)</span>
                      </div>
                      <div className="flex relative">
                        <span className={clsx(sinShown ? "hidden" : " ")}>
                          <img
                            onClick={toggleSinVisiblity}
                            src="assets/eye-icon.svg"
                            className={clsx(
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <span className={clsx(sinShown ? " " : "hidden")}>
                          <img
                            onClick={toggleSinVisiblity}
                            src="assets/eye-slash-icon.svg"
                            className={clsx(
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.sin ? "error" : "",
                            "mt-2 w:[220px] md:w-[305px]"
                          )}
                          disabled={!applicant.isProspect}
                          type={sinShown ? "text" : "password"}
                          autoComplete="new-password"
                          {...methods.register("sin", {
                            validate: {
                              sinValidator: (v) => {
                                if (v == "" || v == undefined) {
                                  return true;
                                } else {
                                  return isValidSin(v) == true;
                                }

                              },
                            }
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.sin?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.sin.message}
                        </span>
                      )}
                      {methods.formState?.errors?.sin?.type == "sinValidator" && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          Enter a valid SIN
                        </span>
                      )}
                    </div>
                  }
                </>}
              <div>
                <div className="mt-5">
                  <p className="text-sm">Email Address</p>
                  <div className="flex relative">
                    <span>
                      <img
                        src="assets/error-icon.svg"
                        className={clsx(
                          methods.formState?.errors?.email ? "" : "hidden",
                          "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                      />
                    </span>
                    <input
                      className={clsx(
                        methods.formState?.errors?.email?.message
                          ? "error"
                          : "",
                        "mt-2 w:[220px] md:w-[305px]"
                      )}
                      type="text"
                      id="email"
                      disabled={applicant.isProspect}
                      {...methods.register("email", {
                        required: "Enter your email address",
                        pattern: {
                          value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                          message: "Email address must be a valid address",
                        },
                      })}
                    />
                  </div>
                  {methods.formState?.errors?.email?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.email.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex md:flex-row flex-col">
                <div className={style.twoColFirst}>
                  <p className="mt-5 text-sm">Marital Status</p>
                  <div className="flex relative">
                    <span>
                      <img
                        src="assets/error-icon.svg"
                        className={clsx(
                          methods.formState?.errors?.maritalStatus ? "" : "hidden",
                          "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                      />
                    </span>
                    <select
                      id="maritalStatus"
                      {...methods.register("maritalStatus", {
                        required: "Select an option",
                      })}
                      defaultValue=""
                      className={clsx(
                        methods.formState?.errors?.maritalStatus?.message ? "error" : "",
                        "mt-2 pt-2.5"
                      )}
                      style={{ maxWidth: 305 }}
                    >
                      <option hidden value="">
                        Choose...
                      </option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="separated">Separated</option>
                      <option value="divorced">Divorced</option>
                      <option value="common_law">Common-Law</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  {methods.formState?.errors?.maritalStatus?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.maritalStatus.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex md:flex-row flex-col">
                <div className={style.twoColFirst}>
                  <p className="mt-5 text-sm">Number of dependents</p>
                  <div className="flex relative">
                    <span>
                      <img
                        src="assets/error-icon.svg"
                        className={clsx(
                          methods.formState?.errors?.numOfDependents
                            ? ""
                            : "hidden",
                          "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                      />
                    </span>
                    <input
                      className={clsx(
                        methods.formState?.errors?.numOfDependents?.message
                          ? "error"
                          : "",
                        "mt-2 w:[220px] md:w-[305px]"
                      )}
                      {...methods.register("numOfDependents", {
                        required: "Enter the number of dependents.",
                        validate: {
                          numOfDependents: (c) => {
                            if (+c >= 0 && +c <= 20) {
                              return true
                            } else {
                              return "Number of dependents must be between 0 and 20"
                            }
                          },
                        },
                      })}
                      type="number"
                      id="dependentCount"
                      max={20}
                    />
                  </div>
                  {methods.formState?.errors?.numOfDependents?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.numOfDependents.message}
                    </span>
                  )}
                </div>
                <div>
                  <p className="mt-5 text-sm">Citizenship Status</p>
                  <div className="flex relative">
                    <span>
                      <img
                        src="assets/error-icon.svg"
                        className={clsx(
                          methods.formState?.errors?.citizenshipStatus ? "" : "hidden",
                          "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                      />
                    </span>
                    <select
                      id="citizenshipStatus"
                      {...methods.register("citizenshipStatus", {
                        required: "Select an option",
                      })}
                      defaultValue=""
                      className={clsx(
                        methods.formState?.errors?.citizenshipStatus?.message ? "error" : "",
                        "mt-2 pt-2.5"
                      )}
                      style={{ maxWidth: 305 }}
                    >
                      <option hidden value="">
                        Choose...
                      </option>
                      <option value="citizen">Citizen</option>
                      <option value="resident">Permanent Resident</option>
                      <option value="workvisa">Work Visa</option>
                      <option value="studentvisa">Student Visa</option>
                      <option value="nonresident">Non-Resident</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {methods.formState?.errors?.citizenshipStatus?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.citizenshipStatus.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex md:flex-row flex-col">
                {application.applicationCategory == applicationCategory.Buy &&
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">Are you a first-time home buyer?</p>
                    <div className="grid grid-flow-row sm:grid-flow-col">
                      <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                        <li className="relative">
                          <input
                            type="radio"
                            value="yes"
                            id="yes-first-time-buyer"
                            {...methods.register("firstTimeBuyer", {
                              required: 'Select an option',
                              onChange: firstTimeBuyerChangeHandler,
                            })}
                            checked={firstTimeHomeBuyer === "yes"}
                            onChange={firstTimeBuyerChangeHandler}
                            className="sr-only peer "
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.firstTimeBuyer?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="yes-first-time-buyer"
                          >
                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                          </label>
                        </li>
                        <li className="relative">
                          <input
                            type="radio"
                            value="no"
                            id="no-first-time-buyer"
                            {...methods.register("firstTimeBuyer", {
                              required: 'Select an option',
                              onChange: firstTimeBuyerChangeHandler,
                            })}
                            checked={firstTimeHomeBuyer === "no"}
                            onChange={firstTimeBuyerChangeHandler}
                            className="sr-only peer leading-6"
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.firstTimeBuyer?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="no-first-time-buyer"
                          >
                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                          </label>
                        </li>
                      </ul>
                    </div>
                    {methods.formState?.errors?.firstTimeBuyer?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.firstTimeBuyer.message}
                      </span>
                    )}
                  </div>
                }
                <div>
                  <p className="mt-5 text-sm">Did you arrive in Canada in the last 5 years?</p>
                  <div className="grid grid-flow-row sm:grid-flow-col">
                    <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                      <li className="relative">
                        <input
                          type="radio"
                          value="yes"
                          id="yes-new-resident"
                          {...methods.register("newToCanada", {
                            required: 'Select an option',
                            onChange: recentResidentChangeHandler,
                          })}
                          checked={newToCanada === "yes"}
                          onChange={recentResidentChangeHandler}
                          className="sr-only peer "
                        />
                        <label
                          className={clsx(
                            style.phoneLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                            methods.formState?.errors?.newToCanada?.message
                              ? style.labelError
                              : ""
                          )}
                          htmlFor="yes-new-resident"
                        >
                          <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          type="radio"
                          value="no"
                          id="no-new-resident"
                          {...methods.register("newToCanada", {
                            required: 'Select an option',
                            onChange: recentResidentChangeHandler,
                          })}
                          checked={newToCanada === "no"}
                          onChange={recentResidentChangeHandler}
                          className="sr-only peer leading-6"
                        />
                        <label
                          className={clsx(
                            style.phoneLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                            methods.formState?.errors?.newToCanada?.message
                              ? style.labelError
                              : ""
                          )}
                          htmlFor="no-new-resident"
                        >
                          <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  {methods.formState?.errors?.newToCanada?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.newToCanada.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex max-sm:hidden mt-5">
                <div className="mb-5">
                  <button
                    className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                    type="button"
                    onClick={closeEdit}
                  >
                    Cancel
                  </button>
                </div>
                <div className="mb-5">
                  <button
                    type="submit"
                    className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                    disabled={isSaving}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                    Save
                  </button>
                </div>
              </div>
              <div className="flex sm:hidden mt-5">
                <div className="mb-5">
                  <button
                    className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                    type="button"
                    onClick={closeEdit}
                    style={{ minWidth: 90 }}
                  >
                    Cancel
                  </button>
                </div>
                <div className="mb-5">
                  <button
                    type="submit"
                    className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                    disabled={isSaving}
                    style={{ minWidth: 90 }}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                    Save
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        }
      </AnimatePresence>
    </div>
  );
}
